import type { OnDestroy } from '@angular/core';
import { Injectable, Injector } from '@angular/core';

import { EditorState } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';

import type {
  ConfigPlugins,
  IProsemirrorConfig,
  PluginsAPI,
  PluginsEvents,
  EditorButton,
} from '@core/prosemirror-legacy';
import {
  createEditor,
  EMPTY_SELECTION,
  FocusPlugin,
  PlaceholderPlugin,
  StateChangesPlugin,
  EditorMode,
} from '@core/prosemirror-legacy';
import type { EditorJSONState } from '@core/prosemirror-types';

import { EditorView$, StateUpdated$ } from './consts';

interface CreateEditorOptions {
  placeholder?: string;
}

@Injectable()
export class ProsemirrorService<C extends IProsemirrorConfig> implements OnDestroy {
  public editorView: EditorView;

  private ngOnDestroy$ = new Subject<void>();
  private destroyPlugins: () => void;

  constructor(
    private stateUpdated$: StateUpdated$,
    private editorView$: EditorView$,
    private injector: Injector,
  ) {}

  public ngOnDestroy() {
    this.destroyPlugins();
    this.editorView.destroy();
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public getCoreEditorPlugins(options: CreateEditorOptions) {
    return [
      ...(options.placeholder ? [new PlaceholderPlugin(options.placeholder)] : []),
      new StateChangesPlugin(state => this.stateUpdated$.next(state)),
      new FocusPlugin(),
    ];
  }

  public updateState(jsonState: EditorJSONState) {
    const { schema, plugins, storedMarks } = this.editorView.state;
    const state = jsonState
      ? EditorState.fromJSON(this.editorView.state, {
          ...jsonState,
          selection: jsonState.selection || EMPTY_SELECTION,
        })
      : EditorState.create({ schema, plugins, storedMarks });
    this.stateUpdated$.next(state);
    this.editorView.setProps({ state });
  }

  public createEditor<Config extends new (...args: any[]) => IProsemirrorConfig>(
    element: HTMLElement,
    configClass: Config,
    options: {
      mode: EditorMode;
      initialState?: EditorJSONState | null;
      placeholder?: string;
    },
  ) {
    const config: IProsemirrorConfig = this.injector.get(configClass);
    const plugins = [
      ...config.getRenderPlugins(),
      ...(EditorMode.Wysiwyg === options.mode
        ? [
            ...this.getCoreEditorPlugins({ placeholder: options.placeholder }),
            ...config.getEditorPlugins(),
          ]
        : []),
    ];

    config.listenForPluginEvents(plugins);

    const { editorView, schema, destroyPlugins, publicApi, buttons } = createEditor(
      element,
      plugins,
      config,
      {
        mode: options.mode,
        initialState: options.initialState,
      },
    );

    this.editorView$.next(editorView);
    this.editorView = editorView;
    this.destroyPlugins = destroyPlugins;
    if (buttons && (<EditorButton[]>buttons).length) {
      config.registerToolbarButtons?.(<any>buttons);
    }

    // import('prosemirror-dev-tools').then(({ applyDevTools }) => applyDevTools(editorView));

    return {
      view: editorView,
      schema: schema,
      publicApi: <PluginsAPI<ConfigPlugins<C>>>publicApi,
      eventStream$: <Observable<PluginsEvents<ConfigPlugins<C>>>>config.eventStream$,
      sanitizeJSON: config.sanitizeJSON,
    };
  }
}
